import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useDeliveryAddress } from '../context/DeliveryAddressContext';
import { useNotification } from '../context/NotificationContext';
import { API_URL } from '../config/api';
import { getFullImageUrl } from '../utils/imageUtils';
import ActiveOrder from '../components/ActiveOrder';
import SEO from '../components/SEO';
import './Landing.css';

const Landing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { currentAddress, setCurrentAddress } = useDeliveryAddress();
  const { showError } = useNotification();
  const [address, setAddress] = useState('');
  const autocompleteInputRef = useRef(null);
  
  const defaultTitles = ['Ruoka kotiinkuljettuna', 'Burgeri kotiinkuljettuna', 'Pizza kotiinkuljettuna'];
  const [heroLines, setHeroLines] = useState([]);
  const [currentTitleIndex, setCurrentTitleIndex] = useState(0);
  const titles = (heroLines.length > 0 ? heroLines : defaultTitles);
  const [favoriteRestaurants, setFavoriteRestaurants] = useState([]);
  const [favoriteStores, setFavoriteStores] = useState([]);
  const [featuredRestaurants, setFeaturedRestaurants] = useState([]);
  const [featuredStores, setFeaturedStores] = useState([]);
  const [showFeatured, setShowFeatured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingFeatured, setLoadingFeatured] = useState(false);
  const [landingNews, setLandingNews] = useState([]);
  const [landingAds, setLandingAds] = useState([]);
  const [hasStores, setHasStores] = useState(true); // hide Stores button when no stores in DB

  // Animated title (rotate through titles)
  useEffect(() => {
    if (titles.length === 0) return;
    const interval = setInterval(() => {
      setCurrentTitleIndex((prev) => (prev + 1) % titles.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [titles.length]);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 50;
    
    const initAutocomplete = () => {
      if (window.google && window.google.maps && window.google.maps.places && autocompleteInputRef.current) {
        try {
          const autocomplete = new window.google.maps.places.Autocomplete(
            autocompleteInputRef.current,
            { types: ['address'] }
          );

          autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (place.geometry && place.geometry.location) {
              let cityName = '';
              if (place.address_components) {
                for (const component of place.address_components) {
                  if (component.types.includes('locality')) {
                    cityName = component.long_name;
                    break;
                  }
                }
              }
              
              // Show street address instead of city name
              const addressText = place.formatted_address || place.name;
              setAddress(addressText);
              
              const addressData = {
                label: cityName || place.formatted_address,
                street: place.formatted_address,
                city: cityName,
                zipCode: extractZipCode(place.address_components || []),
                coordinates: {
                  lat: place.geometry.location.lat(),
                  lng: place.geometry.location.lng()
                }
              };
              setCurrentAddress(addressData);
              // Fetch featured restaurants and stores when address is selected
              fetchFeaturedRestaurantsAndStores(addressData.coordinates);
            }
          });
        } catch (error) {
          console.error('Error initializing Google Places Autocomplete:', error);
        }
      } else if (retryCount < maxRetries) {
        retryCount++;
        setTimeout(initAutocomplete, 100);
      }
    };

    initAutocomplete();
    
    return () => {
      retryCount = maxRetries;
    };
  }, []);

  // Update address display and featured list when currentAddress changes (e.g. saved address from navbar)
  useEffect(() => {
    if (currentAddress?.street) {
      setAddress(currentAddress.street);
    } else if (currentAddress?.city) {
      setAddress(currentAddress.city);
    }
    if (currentAddress?.coordinates?.lat != null && currentAddress?.coordinates?.lng != null) {
      const lat = parseFloat(currentAddress.coordinates.lat);
      const lng = parseFloat(currentAddress.coordinates.lng);
      if (!isNaN(lat) && !isNaN(lng)) {
        fetchFeaturedRestaurantsAndStores({ lat, lng });
      }
    }
  }, [currentAddress]);

  // Check if any stores exist (hide hero Stores button when none)
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/stores/count`)
      .then((res) => { if (!cancelled) setHasStores((res.data?.count ?? 0) > 0); })
      .catch(() => { if (!cancelled) setHasStores(false); });
    return () => { cancelled = true; };
  }, []);

  // Fetch landing page content (hero lines, news and advertisements)
  useEffect(() => {
    const fetchLandingContent = async () => {
      try {
        const response = await axios.get(`${API_URL}/landing-content`);
        const lines = response.data.heroLines || [];
        setHeroLines(Array.isArray(lines) ? lines.filter(Boolean) : []);
        setLandingNews(response.data.news || []);
        setLandingAds(response.data.advertisements || []);
      } catch (error) {
        console.error('Error fetching landing content:', error);
      }
    };
    fetchLandingContent();
  }, []);

  // Fetch favorite restaurants and stores
  useEffect(() => {
    if (user) {
      if (user?.favoriteRestaurants?.length > 0) {
        fetchFavoriteRestaurants();
      }
      if (user?.favoriteStores?.length > 0) {
        fetchFavoriteStores();
      }
      if (!user?.favoriteRestaurants?.length && !user?.favoriteStores?.length) {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchFavoriteRestaurants = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/favorites`);
      setFavoriteRestaurants(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching favorite restaurants:', error);
      setLoading(false);
    }
  };

  const fetchFavoriteStores = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/favorite-stores`);
      setFavoriteStores(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching favorite stores:', error);
      setLoading(false);
    }
  };

  const extractZipCode = (components) => {
    for (const component of components) {
      if (component.types && component.types.includes('postal_code')) {
        return component.long_name;
      }
    }
    return '';
  };

  const handleUseLocation = async () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          
          if (window.google && window.google.maps) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat: loc.lat, lng: loc.lng } }, (results, status) => {
              if (status === 'OK' && results[0]) {
                let cityName = '';
                const addressComponents = results[0].address_components || [];
                for (const component of addressComponents) {
                  if (component.types.includes('locality')) {
                    cityName = component.long_name;
                    break;
                  }
                }
                
                // Show street address instead of city name
                const addressText = results[0].formatted_address;
                setAddress(addressText);
                
                const addressData = {
                  label: cityName || results[0].formatted_address,
                  street: results[0].formatted_address || '',
                  city: cityName,
                  zipCode: extractZipCode(addressComponents),
                  coordinates: loc
                };
                setCurrentAddress(addressData);
                // Fetch featured restaurants and stores when location is used
                fetchFeaturedRestaurantsAndStores(loc);
              }
            });
          }
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  };

  const fetchFeaturedRestaurantsAndStores = async (coordinates) => {
    if (!coordinates || !coordinates.lat || !coordinates.lng) return;
    
    setLoadingFeatured(true);
    setShowFeatured(true);
    
    try {
      // Fetch restaurants
      const restaurantsResponse = await axios.get(`${API_URL}/restaurants`, {
        params: {
          lat: coordinates.lat,
          lng: coordinates.lng
        }
      });
      setFeaturedRestaurants(restaurantsResponse.data.slice(0, 3));
      
      // Fetch stores
      const storesResponse = await axios.get(`${API_URL}/stores`, {
        params: {
          lat: coordinates.lat,
          lng: coordinates.lng
        }
      });
      setFeaturedStores(storesResponse.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching featured restaurants and stores:', error);
    } finally {
      setLoadingFeatured(false);
    }
  };

  const handleAddressEnter = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // If address is already set from autocomplete, fetch featured items
      if (currentAddress && currentAddress.coordinates) {
        fetchFeaturedRestaurantsAndStores(currentAddress.coordinates);
        return;
      }
      
      // If user typed manually, try to geocode the address
      if (address.trim() && window.google && window.google.maps) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: address.trim() }, (results, status) => {
          if (status === 'OK' && results[0] && results[0].geometry) {
            let cityName = '';
            const addressComponents = results[0].address_components || [];
            for (const component of addressComponents) {
              if (component.types.includes('locality')) {
                cityName = component.long_name;
                break;
              }
            }
            
            const addressData = {
              label: cityName || results[0].formatted_address,
              street: results[0].formatted_address,
              city: cityName,
              zipCode: extractZipCode(addressComponents),
              coordinates: {
                lat: results[0].geometry.location.lat(),
                lng: results[0].geometry.location.lng()
              }
            };
            setCurrentAddress(addressData);
            setAddress(results[0].formatted_address);
            fetchFeaturedRestaurantsAndStores(addressData.coordinates);
          } else {
            showError('Address not found. Please select an address from the suggestions.');
          }
        });
      }
    }
  };

  const renderStars = (rating) => {
    if (rating == null || rating <= 0) return null;
    const stars = [];
    const fullStars = Math.min(5, Math.max(0, Math.round(rating)));
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <i key={i} className={i <= fullStars ? 'fas fa-star' : 'far fa-star'}></i>
      );
    }
    return <span className="rating-stars">{stars}</span>;
  };

  const isFavoriteRestaurant = (restaurantId) => {
    return user?.favoriteRestaurants?.includes(restaurantId) || false;
  };

  const isFavoriteStore = (storeId) => {
    return user?.favoriteStores?.includes(storeId) || false;
  };

  const handleToggleFavoriteRestaurant = async (e, restaurantId) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const isFavorite = isFavoriteRestaurant(restaurantId);
      
      if (isFavorite) {
        await axios.delete(`${API_URL}/users/favorites/${restaurantId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Update local state
        setFavoriteRestaurants(prev => prev.filter(r => r._id !== restaurantId));
        // Update user context
        if (user.favoriteRestaurants) {
          user.favoriteRestaurants = user.favoriteRestaurants.filter(r => r.toString() !== restaurantId);
          updateUser(user);
        }
      } else {
        await axios.post(`${API_URL}/users/favorites/${restaurantId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Refetch to get updated restaurant data
        const response = await axios.get(`${API_URL}/users/favorites`);
        setFavoriteRestaurants(response.data);
        // Update user context
        if (!user.favoriteRestaurants) {
          user.favoriteRestaurants = [];
        }
        user.favoriteRestaurants.push(restaurantId);
        updateUser(user);
      }
    } catch (error) {
      console.error('Error toggling favorite restaurant:', error);
    }
  };

  const handleToggleFavoriteStore = async (e, storeId) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const isFavorite = isFavoriteStore(storeId);
      
      if (isFavorite) {
        await axios.delete(`${API_URL}/users/favorite-stores/${storeId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Update local state
        setFavoriteStores(prev => prev.filter(s => s._id !== storeId));
        // Update user context
        if (user.favoriteStores) {
          user.favoriteStores = user.favoriteStores.filter(s => s.toString() !== storeId);
          updateUser(user);
        }
      } else {
        await axios.post(`${API_URL}/users/favorite-stores/${storeId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Refetch to get updated store data
        const response = await axios.get(`${API_URL}/users/favorite-stores`);
        setFavoriteStores(response.data);
        // Update user context
        if (!user.favoriteStores) {
          user.favoriteStores = [];
        }
        user.favoriteStores.push(storeId);
        updateUser(user);
      }
    } catch (error) {
      console.error('Error toggling favorite store:', error);
    }
  };

  return (
    <div className="landing-container">
      <SEO
        title="Ruoka ja kaupat kotiinkuljetuksella"
        description="Tilaa ruoka ravintolasta tai päivittäistavarat kaupasta kotiin. Pussikuskit – ruokalähetys ja kauppatoimitus yhdessä paikassa."
        path="/"
      />
      {/* Hero Section */}
      <div className="landing-hero">
        <div className="hero-content">
          <h1 className="animated-title">
            <span key={currentTitleIndex} className="title-text">{titles[currentTitleIndex % Math.max(1, titles.length)]}</span>
          </h1>
          <p className="hero-tagline">{t('landing.tagline')}</p>
          <div className="address-selection">
            <div className="address-input-wrapper">
              <button onClick={handleUseLocation} className="location-btn-inline" type="button" aria-label={t('nav.useCurrentLocation') || 'Use Current Location'}>
                <i className="fas fa-crosshairs"></i>
              </button>
              <input
                ref={autocompleteInputRef}
                type="text"
                placeholder={t('home.enterAddress') || 'Syötä toimitusosoitteesi'}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                onKeyPress={handleAddressEnter}
                className="address-input-hero"
              />
              <i className="fas fa-map-marker-alt address-icon" aria-hidden="true"></i>
            </div>
            <Link to="/restaurants" className="restaurants-btn">
              {t('nav.restaurants')}
            </Link>
            {hasStores && (
              <Link to="/stores" className="restaurants-btn">
                {t('nav.stores')}
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Active Order - before featured restaurants and stores */}
      <div className="container landing-active-order-wrap">
        <ActiveOrder />
      </div>

      {/* Featured Restaurants and Stores Section */}
      {showFeatured && (
        <div className="featured-section">
          <div className="container">
            {loadingFeatured ? (
              <div className="loading-message">{t('landing.loadingFeatured')}</div>
            ) : (
              <>
                {featuredRestaurants.length > 0 && (
                  <section className="landing-section">
                    <h2>{t('landing.featuredRestaurants')}</h2>
                    <div className="restaurants-grid">
                      {featuredRestaurants.map((restaurant) => (
                        <Link key={restaurant._id} to={`/restaurant/${restaurant._id}`} className="restaurant-card">
                          {restaurant.image && (
                            <div className="restaurant-image-container">
                              <img src={getFullImageUrl(restaurant.image)} alt={restaurant.name} className="restaurant-image" loading="lazy" decoding="async" />
                            </div>
                          )}
                          <div className="restaurant-info">
                            <div className="restaurant-title-row">
                              <h3>{restaurant.name}</h3>
                              {(restaurant.settings?.displayStarRatings !== false && restaurant.rating != null && restaurant.rating > 0) ? renderStars(restaurant.rating) : null}
                            </div>
                            {restaurant.distance && (
                              <p className="restaurant-distance">{t('landing.kmAway', { distance: restaurant.distance.toFixed(1) })}</p>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}
                {featuredStores.length > 0 && (
                  <section className="landing-section">
                    <h2>{t('landing.featuredStores')}</h2>
                    <div className="stores-grid">
                      {featuredStores.map((store) => (
                        <Link key={store._id} to={`/store/${store._id}`} className="store-card">
                          {store.image && (
                            <div className="store-image-container">
                              <img src={getFullImageUrl(store.image)} alt={store.name} className="store-image" loading="lazy" decoding="async" />
                            </div>
                          )}
                          <div className="store-info">
                            <div className="restaurant-title-row">
                              <h3>{store.name}</h3>
                              {(store.settings?.displayStarRatings !== false && store.rating != null && store.rating > 0) ? renderStars(store.rating) : null}
                            </div>
                            {store.distance && (
                              <p className="store-distance">{t('landing.kmAway', { distance: store.distance.toFixed(1) })}</p>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}
                {!loadingFeatured && featuredRestaurants.length === 0 && featuredStores.length === 0 && (
                  <div className="no-results-message">
                    <p>{t('landing.noRestaurantsOrStoresNearby')}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <div className="landing-content">
        <div className="container">
          {/* Favorite Restaurants */}
          {user && favoriteRestaurants.length > 0 && (
            <section className="landing-section">
              <h2>{t('landing.favoriteRestaurants') || 'Suosikki ravintolasi'}</h2>
              <div className="restaurants-grid">
                {favoriteRestaurants.slice(0, 6).map((restaurant) => (
                  <Link key={restaurant._id} to={`/restaurant/${restaurant._id}`} className="restaurant-card">
                    {restaurant.image && (
                      <div className="restaurant-image-container">
                        <img src={getFullImageUrl(restaurant.image)} alt={restaurant.name} className="restaurant-image" loading="lazy" decoding="async" />
                        {user && (
                          <button
                            onClick={(e) => handleToggleFavoriteRestaurant(e, restaurant._id)}
                            className={`favorite-btn ${isFavoriteRestaurant(restaurant._id) ? 'active' : ''}`}
                            title={isFavoriteRestaurant(restaurant._id) ? 'Remove from favorites' : 'Add to favorites'}
                          >
                            <i className={`fas fa-heart ${isFavoriteRestaurant(restaurant._id) ? 'active' : ''}`}></i>
                          </button>
                        )}
                      </div>
                    )}
                    <div className="restaurant-info">
                      <div className="restaurant-title-row">
                        <h3>{restaurant.name}</h3>
                        {(restaurant.settings?.displayStarRatings !== false && restaurant.rating != null && restaurant.rating > 0) ? renderStars(restaurant.rating) : null}
                      </div>
                      {restaurant.description && (
                        <p className="restaurant-description">{restaurant.description}</p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Favorite Stores */}
          {user && favoriteStores.length > 0 && (
            <section className="landing-section">
              <h2>{t('landing.favoriteStores') || 'Suosikki kaupat'}</h2>
              <div className="stores-grid">
                {favoriteStores.slice(0, 6).map((store) => (
                  <Link key={store._id} to={`/store/${store._id}`} className="store-card">
                    {store.image && (
                      <div className="store-image-container">
                        <img src={getFullImageUrl(store.image)} alt={store.name} className="store-image" loading="lazy" decoding="async" />
                        {user && (
                          <button
                            onClick={(e) => handleToggleFavoriteStore(e, store._id)}
                            className={`favorite-btn ${isFavoriteStore(store._id) ? 'active' : ''}`}
                            title={isFavoriteStore(store._id) ? 'Remove from favorites' : 'Add to favorites'}
                          >
                            <i className={`fas fa-heart ${isFavoriteStore(store._id) ? 'active' : ''}`}></i>
                          </button>
                        )}
                      </div>
                    )}
                    <div className="store-info">
                      <div className="restaurant-title-row">
                        <h3>{store.name}</h3>
                        {(store.settings?.displayStarRatings !== false && store.rating != null && store.rating > 0) ? renderStars(store.rating) : null}
                      </div>
                      {store.description && (
                        <p className="store-description">{store.description}</p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Advertisement Section */}
          {(landingAds.length > 0) && (
            <section className="landing-section">
              <h2>{t('landing.advertisements') || 'Mainokset'}</h2>
              <div className="advertisements-grid">
                {landingAds.map((ad, index) => (
                  <div key={index} className="ad-card">
                    {ad.link ? (
                      <a href={ad.link} target="_blank" rel="noopener noreferrer" className="ad-card-link">
                        <img src={getFullImageUrl(ad.image)} alt={ad.title} loading="lazy" decoding="async" onError={e => { e.target.style.display = 'none'; }} />
                        <p>{ad.title}</p>
                      </a>
                    ) : (
                      <>
                        <img src={getFullImageUrl(ad.image)} alt={ad.title} loading="lazy" decoding="async" onError={e => { e.target.style.display = 'none'; }} />
                        <p>{ad.title}</p>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* News Section */}
          {(landingNews.length > 0) && (
            <section className="landing-section">
              <h2>{t('landing.news') || 'Uutiset'}</h2>
              <div className="news-list">
                {landingNews.map((item, index) => (
                  <article key={index} className="news-item">
                    <h3>{item.title}</h3>
                    <p>{item.body}</p>
                    {item.date && (
                      <span className="news-date">
                        {new Date(item.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                      </span>
                    )}
                  </article>
                ))}
              </div>
            </section>
          )}

          {/* Social Media – Instagram & Facebook */}
          <section className="landing-section landing-section-social">
            <h2>{t('landing.socialMedia') || 'Sosiaalinen media'}</h2>
            <div className="social-feed">
              <div className="social-ctas">
                <div className="social-instagram-cta">
                  <div className="social-header">
                    <i className="fab fa-instagram" aria-hidden="true"></i>
                    <span>@_pussikuskit</span>
                  </div>
                  <p className="social-cta-text">
                    {t('landing.followUsInstagram') || 'Seuraa meitä Instagramissa – tarjoukset, uutiset ja ennakkotilaukset.'}
                  </p>
                  <a
                    href="https://www.instagram.com/_pussikuskit/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-follow-btn social-follow-btn-instagram"
                  >
                    {t('landing.followOnInstagram') || 'Seuraa @_pussikuskit'}
                  </a>
                </div>
                <div className="social-facebook-cta">
                  <div className="social-header">
                    <i className="fab fa-facebook" aria-hidden="true"></i>
                    <span>Pussikuskit</span>
                  </div>
                  <p className="social-cta-text">
                    {t('landing.followUsFacebook') || 'Seuraa meitä Facebookissa – uutiset ja yhteisö.'}
                  </p>
                  <a
                    href="https://www.facebook.com/profile.php?id=61586975896090"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-follow-btn social-follow-btn-facebook"
                  >
                    {t('landing.followOnFacebook') || 'Seuraa Facebookissa'}
                  </a>
                </div>
              </div>
              <div className="social-instagram-feed">
                <iframe
                  title={t('landing.instagramFeed') || 'Instagram @_pussikuskit -feed'}
                  src="https://www.instagram.com/_pussikuskit/embed"
                  className="instagram-embed"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Landing;
